import React, { useEffect, useState } from 'react';
import api from '../services/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import deleteIcon from '../assets/images/icons/delete_icon.svg';
import '../styles/floatingForm.css';

const UpdateItineraryInPage = () => {

  const navigate = useNavigate();

  // Get IDs directly from URL
  const { id, locationId } = useParams();

  const [locations, setLocations] = useState([]);
  const [packages, setPackages] = useState([]);

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');

  const [itineraryDays, setItineraryDays] = useState([]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);


  // -----------------------------------
  // FETCH DATA
  // -----------------------------------

  useEffect(() => {

    const fetchData = async () => {

      try {

        setFetching(true);

        // Fetch locations
        const locationResponse =
          await api.get('/location/get-all');

        setLocations(
          locationResponse.data.data || []
        );


        // Fetch packages
        const packageResponse =
          await api.get('/product/get-all');

        setPackages(
          packageResponse.data.data || []
        );


        // Fetch itinerary
        const itineraryResponse =
          await api.get(
            `/itinerary/get/${id}/${locationId}`
          );


        const itinerary =
          itineraryResponse.data.data || [];


        if (!itinerary.length) {

          toast.error(
            'Itinerary details not found'
          );

          navigate('/manage-itinerary');

          return;
        }


        // Set package and location
        setSelectedLocation(
          String(itinerary[0].location_id)
        );

        setSelectedPackage(
          String(itinerary[0].card_id)
        );


        // Set itinerary days
        setItineraryDays(
          itinerary.map((el) => ({
            date: el.date || '',
            description: el.description || ''
          }))
        );


      } catch (error) {

        console.error(
          'Error fetching itinerary:',
          error
        );

        toast.error(
          'Error fetching itinerary details'
        );

      } finally {

        setFetching(false);

      }

    };


    if (id && locationId) {
      fetchData();
    }

  }, [id, locationId, navigate]);


  // -----------------------------------
  // VALIDATION
  // -----------------------------------

  const validateForm = () => {

    const newErrors = {};


    if (!selectedPackage) {

      newErrors.selectedPackage =
        'Select a package';

    }


    if (!selectedLocation) {

      newErrors.selectedLocation =
        'Select a location';

    }


    itineraryDays.forEach(
      (entry, index) => {

        if (!entry.date) {

          newErrors[`date-${index}`] =
            'Day is required';

        }


        // ReactQuill gives HTML
        // Check whether actual content exists
        const plainText =
          entry.description
            ?.replace(/<(.|\n)*?>/g, '')
            .trim();


        if (!plainText) {

          newErrors[`description-${index}`] =
            'Description is required';

        }

      }
    );


    return newErrors;

  };


  // -----------------------------------
  // SUBMIT
  // -----------------------------------

  const handleSubmit = async (e) => {

    e.preventDefault();


    const newErrors =
      validateForm();


    if (
      Object.keys(newErrors).length > 0
    ) {

      setErrors(newErrors);

      toast.error(
        'Please fill all required fields'
      );

      return;

    }


    setErrors({});
    setLoading(true);


    const formData =
      new FormData();


    formData.append(
      'location_id',
      selectedLocation
    );


    formData.append(
      'card_id',
      selectedPackage
    );


    itineraryDays.forEach((el) => {

      formData.append(
        'date[]',
        el.date
      );

      formData.append(
        'description[]',
        el.description
      );

    });


    try {

      const response =
        await api.post(
          `/itinerary/update/${id}/${locationId}`,
          formData,
          {
            headers: {
              'Content-Type':
                'multipart/form-data'
            }
          }
        );


      if (
        response.data.status === 'success'
      ) {

        toast.success(
          'Itinerary updated successfully!'
        );


        // Go back to list page
        navigate('/manage-itinerary');

      } else {

        toast.error(
          'Failed to update itinerary'
        );

      }

    } catch (error) {

      console.error(
        'Error updating itinerary:',
        error
      );

      toast.error(
        error.response?.data?.message ||
        'Error updating itinerary'
      );

    } finally {

      setLoading(false);

    }

  };


  // -----------------------------------
  // CANCEL
  // -----------------------------------

  const handleCancel = () => {

    navigate('/manage-itinerary');

  };


  // -----------------------------------
  // ADD DAY
  // -----------------------------------

  const handleAddDay = () => {

    setItineraryDays([
      ...itineraryDays,
      {
        date: '',
        description: ''
      }
    ]);

  };


  // -----------------------------------
  // REMOVE DAY
  // -----------------------------------

  const handleRemoveDay = (index) => {

    setItineraryDays(
      itineraryDays.filter(
        (_, i) => i !== index
      )
    );


    // Remove corresponding errors
    setErrors((prev) => {

      const updated = {
        ...prev
      };

      delete updated[`date-${index}`];
      delete updated[`description-${index}`];

      return updated;

    });

  };


  // -----------------------------------
  // UPDATE DAY
  // -----------------------------------

  const handleDayChange = (
    index,
    field,
    value
  ) => {

    const updated = [
      ...itineraryDays
    ];

    updated[index] = {
      ...updated[index],
      [field]: value
    };

    setItineraryDays(updated);


    // Clear field error
    setErrors((prev) => ({
      ...prev,
      [`${field}-${index}`]: undefined
    }));

  };

  return (
    <div className='floating-form-container'  >
      <div className="heading">
        <h2>Update Itinerary Details</h2>
      </div>

        <form onSubmit={handleSubmit}>
            <div className="form-container">

                <div className="section-1">
                    <div className="input-container-floating-form wid-50">
                        <label htmlFor="name">Select Package *</label>
                        <select className={`form-control ${errors.selectedPackage ? 'is-invalid' : ''}`} value={selectedPackage} onChange={(e) => setSelectedPackage(e.target.value)} disabled  >
                  <option value=''>Select Package</option>
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg?.packagename}
                    </option>
                  ))}
                </select>
                <b>{errors.selectedPackage && <span className='text-danger small'>{errors.selectedPackage}</span>}</b>
                    </div>

                    <div className="input-container-floating-form wid-50">
                        <label htmlFor="email">Select Location *</label>
                        <select
                  className={`form-control ${errors.selectedLocation ? 'is-invalid' : ''}`}
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  disabled
                >
                  <option value="">Select Location</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.location}
                    </option>
                  ))}
                </select>
                <b>{errors.selectedLocation && <span className='text-danger small'>{errors.selectedLocation}</span>}</b>
                    </div>

                    {itineraryDays.map((entry, index) => (
                        <div key={index} className="day-block wid-100">
                            <div className="input-container-floating-form wid-30">
                            <label>Day *</label>
                            <input
                                type="text"
                                className={`form-control ${errors[`date-${index}`] ? 'is-invalid' : ''}`}
                                value={entry.date}
                                onChange={(e) => {
                                const updated = [...itineraryDays];
                                updated[index].date = e.target.value;
                                setItineraryDays(updated);
                                }}
                            />
                            <b>{errors[`date-${index}`] && <span className='text-danger small'>{errors[`date-${index}`]}</span>}</b>
                            </div>

                            <div className="input-container-floating-form wid-100">
                                {index > 0 && (
                                <button type="button" className="remove-btn remove-day" onClick={() => handleRemoveDay(index)}>
                                    <img src={deleteIcon} alt="delete" />
                                </button>
                                )}
                            </div>

                            <div className="input-container-floating-form wid-100">
                            <label>Description *</label>
                            <ReactQuill
                                value={entry.description}
                                onChange={(value) => {
                                const updated = [...itineraryDays];
                                updated[index].description = value;
                                setItineraryDays(updated);
                                }}
                                className={`react-quill ${errors[`description-${index}`] ? 'is-invalid' : ''}`}
                                placeholder='Enter Description'
                            />
                            <b>{errors[`description-${index}`] && <span className='text-danger small'>{errors[`description-${index}`]}</span>}</b>
                            </div>

                            <hr></hr>
                        </div>
                        ))}

                        <button type="button" onClick={handleAddDay} className='add-day'>+ Add Day</button>
                </div>
            </div>
            <div className="form-btn-container">

                <button type='submit'>Update</button>
                <button type='reset' onClick={handleCancel}>Cancel</button>

            </div>


        </form>

    </div>
  );
};

export default UpdateItineraryInPage;


